/**
 * SceneObjectPicker - A picker component for selecting SceneObjects as action targets
 *
 * Maintains a runtime reference to the SceneObject, ensuring that if the object
 * moves in the hierarchy, the correct TransformPath will be serialized on export.
 *
 * All paths are relative to ensure portability when exporting/importing assets.
 */

import React, { useEffect, useMemo } from 'react';
import type { SceneObject, TransformPath } from 'trace-viewer';
import { isEngineRoot } from 'trace-viewer/editor';
import type { TraceScene } from 'trace-viewer/scene';

/**
 * Compute the transform path for a SceneObject.
 * Always uses relative paths when a context is provided to ensure portability.
 *
 * Delegates to TraceScene.getRelativePath() for the actual computation.
 *
 * @param sceneObject - The target object to create a path for
 * @param traceScene - The TraceScene instance
 * @param contextObject - Optional context object for relative path computation
 */
export function getTransformPath(
    sceneObject: SceneObject,
    traceScene: TraceScene | null,
    contextObject?: SceneObject | null
): TransformPath {
    if (contextObject && traceScene?.getRelativePath) {
        return traceScene.getRelativePath(sceneObject, contextObject);
    }
    // Without context, fall back to object name (legacy behavior for backwards compatibility)
    return sceneObject.name;
}

/**
 * Reference to a SceneObject that computes its TransformPath dynamically.
 * Use this instead of storing path strings directly to handle hierarchy changes.
 */
export interface SceneObjectRef {
    /** The runtime SceneObject reference */
    sceneObject: SceneObject | null;
    /** The display name (cached for UI) */
    displayName: string;
    /** Get the current TransformPath (computed dynamically) */
    getPath: () => TransformPath;
}

/**
 * Create a SceneObjectRef from either a SceneObject or a path string.
 *
 * @param input - The target object or path string
 * @param traceScene - The TraceScene instance
 * @param contextObject - Optional context object for computing relative paths
 */
export function createSceneObjectRef(
    input: SceneObject | string | null | undefined,
    traceScene: TraceScene | null,
    contextObject?: SceneObject | null
): SceneObjectRef {
    if (!input) {
        return {
            sceneObject: null,
            displayName: '',
            getPath: () => ''
        };
    }

    if (typeof input === 'string') {
        // Resolve path to SceneObject
        const sceneObject = traceScene?.resolveSceneObject(input) ?? null;
        return {
            sceneObject,
            displayName: sceneObject?.name ?? input,
            getPath: () => sceneObject ? getTransformPath(sceneObject, traceScene, contextObject) : input
        };
    }

    // Direct SceneObject reference
    return {
        sceneObject: input,
        displayName: input.name,
        getPath: () => getTransformPath(input, traceScene, contextObject)
    };
}

export interface SceneObjectPickerProps {
    /** Current value - can be a TransformPath string or empty */
    value: TransformPath | undefined;
    /** Callback when selection changes - provides the SceneObject reference */
    onChange: (ref: SceneObjectRef) => void;
    /** Placeholder text when no selection */
    placeholder?: string;
    /** Optional: exclude certain objects from the list */
    excludeNames?: string[];
    /** Optional: the context object reference (for showing "self" option and relative paths) */
    contextObject?: SceneObject | null;
    /** Optional: only list objects passing this predicate (hidden objects still contribute hierarchy) */
    filter?: (obj: SceneObject) => boolean;
}

/** Minimal hierarchy surface of SceneObject needed to order a picker list. */
export interface HierarchyNode<T> {
    getChildren(): T[];
    getParent(): T | null;
}

/**
 * Order `objects` in scene-hierarchy (depth-first) order, assigning each visible
 * object an indentation depth that counts only its VISIBLE ancestors. Invisible
 * objects (filtered/excluded) are traversed but not emitted, so e.g. a filtered
 * list of state-group owners nests child groups directly under their parent
 * group even when non-group objects sit between them.
 */
export function orderHierarchically<T extends HierarchyNode<T>>(
    objects: T[],
    isVisible: (obj: T) => boolean
): Array<{ obj: T; depth: number }> {
    const inSet = new Set(objects);
    const result: Array<{ obj: T; depth: number }> = [];

    const visit = (obj: T, depth: number): void => {
        const visible = inSet.has(obj) && isVisible(obj);
        if (visible) result.push({ obj, depth });
        const childDepth = visible ? depth + 1 : depth;
        for (const child of obj.getChildren()) visit(child, childDepth);
    };

    // Roots = objects with no ancestor in the set (direct-parent check is not
    // enough: an object whose parent is missing from the set but whose
    // grandparent is present would be emitted twice).
    const hasAncestorInSet = (obj: T): boolean => {
        for (let p = obj.getParent(); p; p = p.getParent()) {
            if (inSet.has(p)) return true;
        }
        return false;
    };
    for (const root of objects.filter(o => !hasAncestorInSet(o))) visit(root, 0);
    return result;
}

// Special value for "self" option
const SELF_VALUE = '__SELF__';

/**
 * SceneObjectPicker component - native select for proper UX
 * Uses native <select> for correct escape, click-away, and scroll behavior
 */
export const SceneObjectPicker: React.FC<SceneObjectPickerProps> = ({
    value,
    onChange,
    placeholder = 'self',
    excludeNames = [],
    contextObject,
    filter
}) => {
    // Use array to handle duplicate names correctly
    const [sceneObjects, setSceneObjects] = React.useState<SceneObject[]>([]);
    const [traceScene, setTraceScene] = React.useState<TraceScene | null>(null);

    // Get TraceScene reference - check on mount and when viewer might be ready
    useEffect(() => {
        const getTraceScene = () => {
            const viewer = document.querySelector('trace-viewer');
            return viewer?.traceScene ?? null;
        };

        const scene = getTraceScene();
        if (scene) {
            setTraceScene(scene);
            // Never the engine mount point: it is not a node of the file and
            // cannot be a target, so the list starts at the document root
            // (SCENE_ROOT_SPEC D8).
            setSceneObjects((scene.getSceneObjects?.() || []).filter(o => !isEngineRoot(o)));
        }
    }, []);


    // List objects in scene-hierarchy order with an indentation depth per entry.
    // Exclude context object since it's already shown as "self".
    const sortedObjects = useMemo(() => {
        // Exclude by reference, not just by name (handles duplicates)
        const isVisible = (obj: SceneObject): boolean =>
            !excludeNames.includes(obj.name) && obj !== contextObject && (!filter || filter(obj));

        return orderHierarchically(sceneObjects, isVisible).map(({ obj, depth }) => ({
            name: obj.name,
            // Use relative path from context object
            path: traceScene ? getTransformPath(obj, traceScene, contextObject) : obj.name,
            sceneObject: obj,
            depth,
        }));
    }, [sceneObjects, excludeNames, traceScene, contextObject, filter]);

    // Compute current select value
    const selectValue = useMemo(() => {
        if (!value) return SELF_VALUE;
        return value;
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedPath = e.target.value;

        if (selectedPath === SELF_VALUE) {
            // Clear selection (self)
            onChange(createSceneObjectRef(null, traceScene, contextObject));
        } else {
            // Find the selected scene object by path
            const obj = sortedObjects.find(o => o.path === selectedPath);
            if (obj) {
                onChange(createSceneObjectRef(obj.sceneObject, traceScene, contextObject));
            } else {
                // Fallback: use the path string directly
                onChange(createSceneObjectRef(selectedPath, traceScene, contextObject));
            }
        }
    };

    const selectStyle: React.CSSProperties = {
        flex: 1,
        padding: '3px 6px',
        background: 'rgba(0,0,0,0.3)',
        border: '1px solid rgba(255,255,255,0.3)',
        borderRadius: '3px',
        color: 'white',
        fontSize: '10px',
        cursor: 'pointer',
        minWidth: 0,
        maxWidth: '100%'
    };

    return (
        <select
            value={selectValue}
            onChange={handleChange}
            style={selectStyle}
        >
            {/* Self option */}
            <option value={SELF_VALUE}>
                {placeholder} {contextObject ? `(${contextObject.name})` : ''}
            </option>

            {/* Separator */}
            <option disabled>───────────</option>

            {/* Object list, indented by hierarchy depth (nbsp — <option> ignores CSS padding) */}
            {sortedObjects.map(({ name, path, depth }) => (
                <option key={path} value={path}>
                    {'   '.repeat(depth)}{name}
                </option>
            ))}
        </select>
    );
};

export default SceneObjectPicker;
